import * as CONSTANTS from 'store/constants/payments'
import axios from 'axios'
import {
  getClientSecretPath,
  getBankAccountSetupIntentPath,
  getUserCardsPath,
  getUserCardPath,
  getBankAccountsPath,
  getBankAccountPath,
  getBankAccountDefaultPath,
} from 'utils/path-helpers/api'
import { mapPaymentCardToFront } from 'utils/mappers/backend'
import { openSnackbar } from 'store/actions/common'
import Analytics from '../../../utils/analytics/AnalyticsService'
import { EVENTS } from '../../../utils/analytics/Events'

const getClientPaymentSecretStart = () => ({
  type: CONSTANTS.GET_CLIENT_PAYMENT_SECRET_START,
})

const getClientPaymentSecretEnd = payload => ({
  type: CONSTANTS.GET_CLIENT_PAYMENT_SECRET_END,
  payload,
})

export const getClientPaymentSecret = () => dispatch => {
  dispatch(getClientPaymentSecretStart())
  return axios
    .post(getClientSecretPath())
    .then(response => {
      dispatch(getClientPaymentSecretEnd(response.data.setup_intent_client_secret))
    })
    .catch(error => {
      dispatch(getClientPaymentSecretEnd(''))
      return Promise.reject(error)
    })
}

const getUserCardsStart = () => ({
  type: CONSTANTS.GET_USER_CARDS_START,
})

const getUserCardsEnd = payload => ({
  type: CONSTANTS.GET_USER_CARDS_END,
  payload,
})

export const getUserCards = hiddenCardPaymentId => dispatch => {
  dispatch(getUserCardsStart())
  return axios
    .get(getUserCardsPath(), {
      params: {
        include_payment_method_id: hiddenCardPaymentId,
      },
    })
    .then(response => {
      const cards = response.data.map(card => mapPaymentCardToFront(card))
      dispatch(getUserCardsEnd(cards))
      return cards
    })
    .catch(error => {
      dispatch(getUserCardsEnd([]))
      return Promise.reject(error)
    })
}

const saveUserCardStart = () => ({
  type: CONSTANTS.SAVE_USER_CARD_START,
})

const saveUserCardEnd = () => ({
  type: CONSTANTS.SAVE_USER_CARD_END,
})

export const saveUserCard = data => (dispatch, getState) => {
  dispatch(saveUserCardStart())
  return axios
    .post(getUserCardsPath(), data)
    .then(response => {
      dispatch(saveUserCardEnd())
      dispatch(openSnackbar('success', data.remember ? 'Card saved' : 'Card added'))
      Analytics.track(EVENTS.CLIENT_ADDED_CARD, {
        card: data.remember !== false || !!getState().payments.userCards.length,
      })
      return response.data
    })
    .catch(error => {
      const errorMessage =
        (error.response && error.response.data && error.response.data.detail) || 'Error while saving card'
      dispatch(openSnackbar('error', errorMessage))
      dispatch(saveUserCardEnd())
      return Promise.reject(error)
    })
}

const updateUserCardStart = () => ({
  type: CONSTANTS.UPDATE_USER_CARD_START,
})

const updateUserCardEnd = () => ({
  type: CONSTANTS.UPDATE_USER_CARD_END,
})

export const updateUserCard = (uuid, data) => dispatch => {
  dispatch(updateUserCardStart())
  return axios
    .patch(getUserCardPath(uuid), data)
    .then(() => {
      dispatch(updateUserCardEnd())
    })
    .catch(error => {
      dispatch(updateUserCardEnd())
      return Promise.reject(error)
    })
}

const deleteUserCardStart = () => ({
  type: CONSTANTS.DELETE_USER_CARD_START,
})

const deleteUserCardEnd = () => ({
  type: CONSTANTS.DELETE_USER_CARD_END,
})

export const deleteUserCard = uuid => (dispatch, getState) => {
  dispatch(deleteUserCardStart())
  return axios
    .delete(getUserCardPath(uuid))
    .then(() => {
      dispatch(deleteUserCardEnd())
    })
    .catch(error => {
      const errorMessage =
        (error.response && error.response.data && error.response.data.detail) || 'Error while deleting bank account'
      dispatch(openSnackbar('error', errorMessage))
      dispatch(deleteUserCardEnd())
      return Promise.reject(error)
    })
}

const getUserCardStart = () => ({
  type: CONSTANTS.GET_USER_CARD_START,
})

const getUserCardEnd = () => ({
  type: CONSTANTS.GET_USER_CARD_END,
})

export const getUserCard = uuid => dispatch => {
  dispatch(getUserCardStart())
  return axios
    .get(getUserCardPath(uuid))
    .then(response => {
      dispatch(getUserCardEnd())
      return mapPaymentCardToFront(response.data)
    })
    .catch(error => {
      dispatch(getUserCardEnd([]))
      return Promise.reject(error)
    })
}

// Bank Account Setup Intent Actions
const getBankAccountSetupIntentStart = () => ({
  type: CONSTANTS.GET_BANK_ACCOUNT_SETUP_INTENT_START,
})

const getBankAccountSetupIntentEnd = payload => ({
  type: CONSTANTS.GET_BANK_ACCOUNT_SETUP_INTENT_END,
  payload,
})

export const getBankAccountSetupIntent = (accountType = null) => (dispatch, getState) => {
  dispatch(getBankAccountSetupIntentStart())
  let finalAccountType = accountType

  if (!finalAccountType) {
    const state = getState()
    const currentUser = state.auth.currentUser
    const contactInfo = state.profileCreation.profileData.contactInfo

    const userCountry = contactInfo?.country || currentUser?.country || 840
    finalAccountType = userCountry === 124 ? 'pad' : 'ach'
  }

  const requestBody = {
    account_type: finalAccountType,
  }

  return axios
    .post(getBankAccountSetupIntentPath(), requestBody)
    .then(response => {
      dispatch(getBankAccountSetupIntentEnd(response.data))
      return response.data
    })
    .catch(error => {
      dispatch(getBankAccountSetupIntentEnd({}))
      return Promise.reject(error)
    })
}

// Bank Account Actions
const getBankAccountsStart = () => ({
  type: CONSTANTS.GET_BANK_ACCOUNTS_START,
})

const getBankAccountsEnd = payload => ({
  type: CONSTANTS.GET_BANK_ACCOUNTS_END,
  payload,
})

export const getBankAccounts = () => dispatch => {
  dispatch(getBankAccountsStart())
  return axios
    .get(getBankAccountsPath())
    .then(response => {
      const bankAccounts = response.data.map(account => account)
      dispatch(getBankAccountsEnd(bankAccounts))
      return bankAccounts
    })
    .catch(error => {
      dispatch(getBankAccountsEnd([]))
      return Promise.reject(error)
    })
}

const saveBankAccountStart = () => ({
  type: CONSTANTS.SAVE_BANK_ACCOUNT_START,
})

const saveBankAccountEnd = () => ({
  type: CONSTANTS.SAVE_BANK_ACCOUNT_END,
})

export const saveBankAccount = data => (dispatch, getState) => {
  dispatch(saveBankAccountStart())
  return axios
    .post(getBankAccountsPath(), data)
    .then(response => {
      dispatch(saveBankAccountEnd())
      dispatch(openSnackbar('success', 'Bank account saved'))
      Analytics.track(EVENTS.CLIENT_ADDED_CARD, {
        card: !!getState().payments.bankAccounts.length,
      })
      return response.data
    })
    .catch(error => {
      const errorMessage =
        (error.response && error.response.data && error.response.data.detail) || 'Error while saving bank account'
      dispatch(openSnackbar('error', errorMessage))
      dispatch(saveBankAccountEnd())
      return Promise.reject(error)
    })
}

// Bank Account Management Actions
const updateBankAccountStart = () => ({
  type: CONSTANTS.UPDATE_BANK_ACCOUNT_START,
})

const updateBankAccountEnd = () => ({
  type: CONSTANTS.UPDATE_BANK_ACCOUNT_END,
})

export const updateBankAccount = (paymentMethodId, data) => dispatch => {
  dispatch(updateBankAccountStart())
  return axios
    .patch(getBankAccountPath(paymentMethodId), data)
    .then(response => {
      dispatch(updateBankAccountEnd())
      dispatch(openSnackbar('success', 'Bank account updated'))
      return response.data
    })
    .catch(error => {
      const errorMessage =
        (error.response && error.response.data && error.response.data.detail) || 'Error while updating bank account'
      dispatch(openSnackbar('error', errorMessage))
      dispatch(updateBankAccountEnd())
      return Promise.reject(error)
    })
}

const deleteBankAccountStart = () => ({
  type: CONSTANTS.DELETE_BANK_ACCOUNT_START,
})

const deleteBankAccountEnd = () => ({
  type: CONSTANTS.DELETE_BANK_ACCOUNT_END,
})

export const deleteBankAccount = paymentMethodId => dispatch => {
  dispatch(deleteBankAccountStart())
  return axios
    .delete(getBankAccountPath(paymentMethodId))
    .then(response => {
      dispatch(deleteBankAccountEnd())
      dispatch(openSnackbar('success', 'Bank account deleted'))
      return response.data
    })
    .catch(error => {
      const errorMessage =
        (error.response && error.response.data && error.response.data.detail) || 'Error while deleting bank account'
      dispatch(openSnackbar('error', errorMessage))
      dispatch(deleteBankAccountEnd())
      return Promise.reject(error)
    })
}

// Set Bank Account as Default
const setBankAccountDefaultStart = () => ({
  type: CONSTANTS.SET_BANK_ACCOUNT_DEFAULT_START,
})

const setBankAccountDefaultEnd = () => ({
  type: CONSTANTS.SET_BANK_ACCOUNT_DEFAULT_END,
})

export const setBankAccountDefault = uuid => dispatch => {
  dispatch(setBankAccountDefaultStart())
  return axios
    .post(getBankAccountDefaultPath(uuid))
    .then(response => {
      dispatch(setBankAccountDefaultEnd())
      dispatch(openSnackbar('success', 'Bank account set as default'))
      return response.data
    })
    .catch(error => {
      const errorMessage =
        (error.response && error.response.data && error.response.data.detail) ||
        'Error while setting bank account as default'
      dispatch(openSnackbar('error', errorMessage))
      dispatch(setBankAccountDefaultEnd())
      return Promise.reject(error)
    })
}
